import {
  useAuth,
  useCompanies,
  useCompaniesMutations,
  useCompanyPreferences,
} from '@api'
import { COMPANY_ID_KEY, setCompanyIds } from '@api/resources/auth/tools'
import { Loader } from '@components'
import Button from '@components/buttons/Button'
import Layout from '@components/Layout'
import { Modal } from '@components/Modal'
import CompaniesTable from '@components/tables/CompaniesTable'
import Text from '@components/typography/Text'
import ExclamationIcon from '@public/icons/exclamation.svg'
import PlusIcon from '@public/icons/plus.svg'
import { getRouteToRedirect } from '@tools/homepage'
import { get } from 'local-storage'
import type { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

export const ACQUIRING_PROVIDERS = ['none', 'mono'] as const
export const FISCALIZATION_PROVIDERS = ['none', 'checkbox'] as const
export const RECALCULATION_STATUSES = [
  'PARTIAL_RETURN',
  'NOT_DELIVERED',
] as const

const HomePage = () => {
  const { t } = useTranslation()

  const router = useRouter()

  const companyId = get<string>(COMPANY_ID_KEY)

  const preferencesQuery = useCompanyPreferences()

  const routeToRedirect = getRouteToRedirect(
    Number(companyId),
    preferencesQuery.data,
  )

  useEffect(() => {
    if (routeToRedirect) {
      router.push(routeToRedirect)
    }
  }, [companyId, preferencesQuery.data])

  const companiesQuery = useCompanies()
  const authQuery = useAuth()
  const mutations = useCompaniesMutations()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [confirmModalOpened, setConfirmModalOpened] = useState(false)

  if (!companiesQuery.isSuccess || !authQuery.isSuccess) {
    return <Loader queries={[companiesQuery, authQuery]} />
  }

  const canDelete = authQuery.data.role === 'RINKAI_OWNER'

  const selectedCompany = companiesQuery.data.companiesMap.get(selectedId ?? 0)

  if (
    preferencesQuery.data &&
    preferencesQuery.data.settings &&
    preferencesQuery.data.settings.homePage &&
    preferencesQuery.data.settings.homePage !== '/companies'
  ) {
    return null
  }

  const onClickRow = (companyId: number) => {
    const company = companiesQuery.data.companiesMap.get(companyId)
    // Orders page resolves preferences by the stored external id
    setCompanyIds(company?.companyExternalId ?? '', companyId)
    router.push(`/companies/${companyId}/orders`)
  }

  const onClickDelete = (companyId: number) => {
    setSelectedId(companyId)
    setConfirmModalOpened(true)
  }

  const onCloseConfirmModal = () => {
    setConfirmModalOpened(false)
    setTimeout(() => {
      setSelectedId(null)
    }, 500)
  }

  const onClickConfirm = async () => {
    if (!selectedId) return
    await mutations.deleteCompany.mutateAsync(selectedId)
    setConfirmModalOpened(false)
  }

  return (
    <>
      <Layout
        title={t('pages.home.title')}
        headerChildren={
          <Button
            onClick={() => router.push('companies/create')}
            label={t('pages.home.create')}
            icon={<PlusIcon className="w-[22px] h-[22px] text-white" />}
          />
        }
      >
        <CompaniesTable
          data={companiesQuery.data.companies}
          onClickRow={onClickRow}
          onClickEdit={companyId => router.push(`companies/${companyId}/edit`)}
          onClickDelete={onClickDelete}
          canDelete={canDelete}
        />
      </Layout>

      <Modal
        open={confirmModalOpened}
        titleIcon={
          <div className="h-10 w-10 bg-danger-100 rounded-full flex items-center justify-center">
            <ExclamationIcon className="w-6 h-6" />
          </div>
        }
        onOpenChange={onCloseConfirmModal}
        title={t('pages.home.delete_company')}
        footerChildren={
          <div className="flex gap-3 justify-between">
            <Button
              onClick={onCloseConfirmModal}
              color="secondary"
              label={t('ui.cancel')}
            />
            <Button
              color="danger"
              onClick={onClickConfirm}
              label={t('ui.delete')}
              disabled={mutations.deleteCompany.isLoading}
            />
          </div>
        }
      >
        <Text
          as="p"
          size="subtitle-default"
          className="px-2 sm:px-5 py-3 text-center"
        >
          {t('pages.home.table.remove_company', {
            name: selectedCompany?.name ?? '',
          })}
        </Text>
      </Modal>
    </>
  )
}

export default HomePage

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!)),
    },
  }
}
